import { useCallback, useRef, useState } from "react";
import cx from "classnames";

import { TableFooterCell } from "../../../base/cells/TableFooterCell";
import { DataTableFooterCellProps as BaseDataTableFooterCellProps } from "../../../base/cells/TableFooterCell";

import styles from "./DataTableCells.module.scss";
import { useDTFocusDispatchContext } from "../context/focus/useDTFocusDispatchContext";
import { formatCellPositionString } from "../util/cellPositionHelpers";

/**
 * Props for the DataTableFooterCell component
 * @extends BaseDataTableFooterCellProps
 */
export type DataTableFooterCellProps = BaseDataTableFooterCellProps & {
  cellPosition: { rowIndex: number; columnIndex: number };
};

/**
 * Data table footer cell with focus management
 * @param props - The props for the DataTableFooterCell component
 * @param props.cellPosition - The cell position of the cell
 * @param props.children - The children of the DataTableFooterCell component
 * @param props.className - The class name of the DataTableFooterCell component
 * @param props.tabIndex - The tab index of the cell
 */
export function DataTableFooterCell(props: DataTableFooterCellProps) {
  const { cellPosition, children, className, ...rest } = props;
  const [tabIndex, setTabIndex] = useState(-1);

  const footerCellRef = useRef<HTMLDivElement>(null);

  const { moveFocusToCell } = useDTFocusDispatchContext();

  const handleFocus = useCallback(() => {
    moveFocusToCell(cellPosition);
    setTabIndex(0);
  }, [moveFocusToCell, cellPosition, setTabIndex]);

  const handleBlur = useCallback(() => {
    setTabIndex(-1);
  }, [setTabIndex]);

  const { jumpFocus, moveFocus } = useDTFocusDispatchContext();

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTableCellElement>) => {
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          moveFocus("up");
          return;
        case "ArrowDown":
          event.preventDefault();
          moveFocus("down");
          return;
        case "ArrowLeft":
          event.preventDefault();
          moveFocus("left");
          return;
        case "ArrowRight":
          event.preventDefault();
          moveFocus("right");
          return;
        case "Home": {
          event.preventDefault();
          jumpFocus("left");
          return;
        }
        case "End": {
          event.preventDefault();
          jumpFocus("right");
          return;
        }
        default:
          break;
      }
    },
    [moveFocus, jumpFocus],
  );

  return (
    <TableFooterCell
      type="data-table"
      {...rest}
      ref={footerCellRef}
      tabIndex={tabIndex}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={cx(styles["data-table-footer-cell"], className)}
      data-cell-pos={formatCellPositionString(cellPosition)}
    >
      {children}
    </TableFooterCell>
  );
}
